import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { TopNav } from "./components/TopNav.js";
import { SiteFooter } from "./components/SiteFooter.js";
import { useLocale } from "./i18n/LocaleContext.js";
import { Home } from "./pages/Home.js";
import { PublicHome } from "./pages/PublicHome.js";
import { About } from "./pages/About.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Build } from "./pages/Build.js";
import { Pricing } from "./pages/Pricing.js";
import { Admin } from "./pages/Admin.js";
import { RequireAuth } from "./components/auth/RequireAuth.js";
import { RequireOwner } from "./components/auth/RequireOwner.js";
import { AIBuilder } from "./pages/AIBuilder.js";
import { GraphicsEditor } from "./pages/GraphicsEditor.js";
import { TemplateMarketplace } from "./pages/TemplateMarketplace.js";
import { CreativeStudio } from "./pages/CreativeStudio.js";
import { VideoStudio } from "./pages/VideoStudio.js";
import { MusicStudio } from "./pages/MusicStudio.js";
import { MarketingStudio } from "./pages/MarketingStudio.js";
import { ARStudio } from "./pages/ARStudio.js";
import { EducationStudio } from "./pages/EducationStudio.js";
import { PatentStudio } from "./pages/PatentStudio.js";
import { ArchitectureStudio } from "./pages/ArchitectureStudio.js";
import { ExtensionStudio } from "./pages/ExtensionStudio.js";
import { PluginWorkspace } from "./pages/PluginWorkspace.js";
import { Login } from "./pages/Login.js";
import { Signup } from "./pages/Signup.js";
import { OrgSettings } from "./pages/OrgSettings.js";
import { SsoCallback } from "./pages/SsoCallback.js";
import { Redeem } from "./pages/Redeem.js";
import { Account } from "./pages/Account.js";
import { ForgotPassword } from "./pages/ForgotPassword.js";
import { PasswordReset } from "./pages/PasswordReset.js";
import { Discover } from "./pages/Discover.js";
import { FeatureDetail } from "./pages/FeatureDetail.js";
import { HelpCenter } from "./pages/HelpCenter.js";
import { Settings } from "./pages/Settings.js";
import { NotFound } from "./pages/NotFound.js";
import { ShortcutsPage } from "./pages/ShortcutsPage.js";
import { TrustPolicy } from "./pages/TrustPolicy.js";
import { ResendVerification } from "./pages/ResendVerification.js";

function AppShell() {
  const { locale, dir } = useLocale();
  const { pathname } = useLocation();
  const hideFooter = pathname.startsWith("/build");
  return (
    <div lang={locale} dir={dir} className="min-h-screen flex flex-col">
      <TopNav />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<PublicHome />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<TrustPolicy policyId="privacy" />} />
          <Route path="/terms" element={<TrustPolicy policyId="terms" />} />
          <Route path="/acceptable-use" element={<TrustPolicy policyId="acceptable-use" />} />
          <Route path="/refund-policy" element={<TrustPolicy policyId="refund-policy" />} />
          <Route path="/security" element={<TrustPolicy policyId="security" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/sso/callback" element={<SsoCallback />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/resend-verification" element={<ResendVerification />} />
          <Route path="/redeem" element={<Redeem />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/features/:id" element={<FeatureDetail />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/pricing" element={<Pricing />} />

          <Route element={<RequireAuth />}>
            <Route path="/app/new" element={<Home />} />
            <Route path="/settings/org" element={<OrgSettings />} />
            <Route path="/account" element={<Account />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/shortcuts" element={<ShortcutsPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/tools" element={<PluginWorkspace />} />
            <Route path="/build/:projectId" element={<Build />} />
            <Route path="/ai-builder" element={<AIBuilder />} />
            <Route path="/templates" element={<TemplateMarketplace />} />
            <Route path="/editor" element={<GraphicsEditor />} />
            <Route path="/studio" element={<CreativeStudio />} />
            <Route path="/studio/video" element={<VideoStudio />} />
            <Route path="/studio/music" element={<MusicStudio />} />
            <Route path="/studio/marketing" element={<MarketingStudio />} />
            <Route path="/studio/ar" element={<ARStudio />} />
            <Route path="/studio/education" element={<EducationStudio />} />
            <Route path="/studio/patent" element={<PatentStudio />} />
            <Route path="/studio/architecture" element={<ArchitectureStudio />} />
            <Route path="/studio/game" element={<ExtensionStudio studioId="game" />} />
            <Route path="/studio/cad" element={<ExtensionStudio studioId="cad" />} />
            <Route path="/studio/legal" element={<ExtensionStudio studioId="legal" />} />
            <Route path="/studio/fintech" element={<ExtensionStudio studioId="fintech" />} />
            <Route path="/studio/healthcare" element={<ExtensionStudio studioId="healthcare" />} />
            <Route path="/studio/mobile" element={<ExtensionStudio studioId="mobile" />} />
            <Route path="/studio/voice" element={<ExtensionStudio studioId="voice" />} />
            <Route path="/studio/data" element={<ExtensionStudio studioId="data" />} />
            <Route path="/studio/localization" element={<ExtensionStudio studioId="localization" />} />
            <Route path="/studio/collab" element={<ExtensionStudio studioId="collab" />} />
            <Route
              path="/admin"
              element={
                <RequireOwner>
                  <Admin />
                </RequireOwner>
              }
            />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      {!hideFooter && <SiteFooter />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
